/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React from 'react'
import {useTranslation} from 'react-i18next'
import clsx from 'clsx'
import Dates from 'utils/Dates'
import {ReservationJournal} from 'types/entities/ReservationJournal'
import {DATE_FORMATS} from "constants/DateFormats";

interface BookingSellerReservationJournalProps {
    journals: ReservationJournal[]
    onShowAll: () => void
    className?: string
}

const BookingSellerReservationJournal: React.FC<
    BookingSellerReservationJournalProps
> = ({ journals, onShowAll, className }) => {
    const { t } = useTranslation()

    return (
        <div
            className={clsx(
                'BookingSellerReservationJournal',
                'UiCard p-2 flex flex-col',
                className
            )}
        >
            <div className="flex items-center justify-between mb-2 pb-1 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                    {t('Journal')}
                </h3>
                <button
                    type="button"
                    className="text-[10px] text-blue-500 hover:underline cursor-pointer"
                    onClick={onShowAll}
                >
                    {t('Show all')}
                </button>
            </div>
            <div className="flex-1 overflow-y-auto pr-1 flex flex-col gap-3">
                {journals && journals.length > 0 ? (
                    [...journals].reverse().map((journal) => (
                        <div
                            key={journal.id}
                            className="flex flex-col border-b border-gray-50 dark:border-gray-800 last:border-0 pb-2"
                        >
                            <div className="flex justify-between items-center mb-1">
                                <span className="text-xs font-bold text-gray-400 uppercase tracking-tight">
                                    {Dates.dayjs(journal.created_at).format(
                                        DATE_FORMATS.DATE_TIME_SEC
                                    )}
                                </span>
                                {journal.driver && (
                                    <span className="text-[10px] bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded text-gray-500">
                                        {journal.driver.name}
                                    </span>
                                )}
                            </div>
                            <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-wrap">
                                {journal.text}
                            </p>
                        </div>
                    ))
                ) : (
                    <div className="flex-1 flex items-center justify-center text-xs text-gray-400 italic">
                        {t('No journal entries')}
                    </div>
                )}
            </div>
        </div>
    )
}

export default BookingSellerReservationJournal
